import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import servicesData from "./Servicesdata.json";
import {
  FaClipboardList,
  FaCogs,
  FaTools,
  FaSyncAlt,
  FaHandshake,
} from "react-icons/fa";

/* ─── tiny hook: animate number count-up ─── */
function useCountUp(target, duration = 1800, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return val;
}

/* ─── Stat card with count-up ─── */
function StatCard({ value, suffix = "", label, started }) {
  const num = useCountUp(value, 1600, started);
  return (
    <div className="flex flex-col items-center justify-center text-center p-6 border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
      <span
        className="text-4xl font-black text-[#e8341c] leading-none"
        style={{ fontFamily: "'Barlow Condensed', 'Arial Black', sans-serif" }}
      >
        {num}
        {suffix}
      </span>
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest mt-2">
        {label}
      </span>
    </div>
  );
}

/* ─── Image gallery with active selector ─── */
function ImageGallery({ images, title }) {
  const [active, setActive] = useState(0);
  return (
    <div className="flex flex-col gap-3">
      {/* Main image */}
      <div className="relative overflow-hidden rounded-2xl aspect-video w-full shadow-xl group max-h-[450px]">
        <img
          key={active}
          src={images[active]}
          alt={title}
          className="w-full h-full object-cover transition-all duration-700 scale-100 group-hover:scale-105"
          style={{ animation: "imgFadeIn 0.5s ease" }}
          onError={(e) => {
            e.target.src =
              "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=900&q=85";
          }}
        />
        {/* Red gradient overlay bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/60 to-transparent" />
        {/* Image counter badge */}
        <div className="absolute top-4 right-4 bg-black/70 text-white text-xs font-bold px-3 py-1 rounded-full backdrop-blur-sm">
          {active + 1} / {images.length}
        </div>
      </div>
      {/* Thumbnails */}
      <div className="grid grid-cols-4 gap-2">
        {images.map((img, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`relative overflow-hidden rounded-xl aspect-video transition-all duration-200 ${
              i === active
                ? "ring-2 ring-[#e8341c] ring-offset-2 opacity-100"
                : "opacity-60 hover:opacity-90"
            }`}
          >
            <img
              src={img}
              alt={`${title} ${i + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src =
                  "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&q=60";
              }}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const statsRef = useRef(null);
  const [statsStarted, setStatsStarted] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);

  const service = servicesData.find((s) => s.slug === slug);

  /* scroll-trigger for stats */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsStarted(true);
      },
      { threshold: 0.3 },
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  /* scroll to top on mount */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const steps = [
    {
      icon: FaClipboardList,
      title: "Survey & Investigation",
      color: "text-gray-600",
      border: "border-gray-400",
      points: [
        "Topographical survey using DGPS and drone mapping",
        "Geological exploration and resource estimation",
        "Soil and rock sampling for geotechnical analysis",
        "Feasibility study and site risk assessment",
      ],
    },
    {
      icon: FaCogs,
      title: "Planning & Design",
      color: "text-orange-500",
      border: "border-orange-500",
      points: [
        "Mine planning including pit design and bench configuration",
        "Haul road design and equipment selection",
        "Crusher and processing plant layout design",
        "Production scheduling and cost optimization",
      ],
    },
    {
      icon: FaTools,
      title: "Pre-Construction",
      color: "text-blue-500",
      border: "border-blue-500",
      points: [
        "Site clearance and land preparation",
        "Mobilization of heavy machinery and workforce",
        "DGMS-approved drilling and blasting plan preparation",
        "Installation of utilities (water, power, safety systems)",
      ],
    },
    {
      icon: FaSyncAlt,
      title: "Execution",
      color: "text-green-500",
      border: "border-green-500",
      points: [
        "Drilling, blasting, excavation, and material handling",
        "Operation of crushing and screening plants",
        "Continuous quality control and production monitoring",
        "Equipment maintenance and operational safety checks",
      ],
    },
    {
      icon: FaHandshake,
      title: "Compliance & Handover",
      color: "text-blue-900",
      border: "border-blue-900",
      points: [
        "Environmental compliance and rehabilitation activities",
        "Final safety inspection and regulatory approvals",
        "Mine closure planning and documentation",
        "Project handover with complete statutory reporting",
      ],
    },
  ];

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <p className="text-2xl font-black text-gray-800">Service not found.</p>
        <button
          onClick={() => navigate("/")}
          className="mt-6 bg-[#e8341c] text-white px-8 py-3 rounded font-bold uppercase tracking-widest hover:bg-[#c42b14] transition-colors"
        >
          Back to Home
        </button>
      </div>
    );
  }

  /* Other services (excluding current) */
  const others = servicesData.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <div
      className="bg-white min-h-screen"
      style={{ fontFamily: "'Barlow', 'Segoe UI', sans-serif" }}
    >
      {/* ══ HERO BANNER ══ */}
      <section
        className="relative w-full overflow-hidden"
        style={{ maxHeight: "380px" }}
      >
        {/* Background image */}
        <img
          src={service.images[0]}
          alt={service.lable}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => {
            e.target.src =
              "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=1400&q=85";
          }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-transparent" />
        {/* Red accent strip */}
        <div className="absolute bottom-0 left-0 w-1 h-full bg-[#e8341c]" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16 flex flex-col justify-end h-full py-16">
          {/* Breadcrumb */}
          <div
            className="flex items-center gap-2 text-sm text-gray-300 mb-5"
            style={{ animation: "fadeUp 0.5s ease both" }}
          >
            <button
              onClick={() => navigate("/")}
              className="hover:text-[#e8341c] transition-colors font-medium"
            >
              Home
            </button>
            <span className="text-gray-500">/</span>
            <button
              onClick={() => navigate("/services")}
              className="hover:text-[#e8341c] transition-colors font-medium"
            >
              Services
            </button>
            <span className="text-gray-500">/</span>
            <span className="text-[#e8341c] font-semibold">
              {service.lable}
            </span>
          </div>

          {/* Category pill */}
          {/* <div
            className="inline-flex items-center gap-2 bg-[#e8341c] text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full w-fit mb-4"
            style={{ animation: "fadeUp 0.55s ease both" }}
          >
            <span>{service.icon}</span>
            <span>{service.category}</span>
          </div> */}

          {/* Title */}
          <h1
            className="text-2xl md:text-3xl tracking-wider font-black text-white leading-none mb-5"
            style={{
              animation: "fadeUp 0.6s ease both",
            }}
          >
            {service.lable}
          </h1>

          {/* Short description */}
          <p
            className="text-gray-300 text-base max-w-xl leading-relaxed"
            style={{ animation: "fadeUp 0.7s ease both" }}
          >
            {service.shortDescription}
          </p>
        </div>
      </section>

      {/* ══ MAIN CONTENT ══ */}
      <section className=" px-4 py-10 sm:px-8 lg:px-16 sm:py-16 ">
        {/* Left: Image Gallery */}
        <div
          className="max-w-7xl mx-auto"
          style={{ animation: "fadeUp 0.5s ease both" }}
        >
          <ImageGallery images={service.images} title={service.lable} />

          {/* Certifications */}
          <div className="mt-8">
            <p className="text-xs font-bold font-krona  uppercase tracking-widest text-gray-800 mb-4 border-b border-gray-100 pb-2">
              Certifications & Standards
            </p>
            <div className="flex flex-wrap gap-2">
              {service.certifications.map((cert) => (
                <span
                  key={cert}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#e8341c] flex-shrink-0" />
                  {cert}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Description + Features */}
        <div className="flex flex-col gap-8 mt-6 max-w-7xl mx-auto">
          {/* Section label */}
          <div>
            <p className="text-xs font-bold font-krona tracking-[0.22em] uppercase text-gray-800 border-b-2 border-[#e8341c] inline-block pb-0.5 mb-4">
              About This Service
            </p>
            <p className="text-gray-600 leading-relaxed text-[15px]">
              {service.fullDescription}
            </p>
          </div>

          {/* Features */}
          <div>
            <p className="text-xs font-krona font-bold tracking-[0.22em] uppercase text-gray-800 border-b-2 border-[#e8341c] inline-block pb-0.5 mb-5">
              What We Offer
            </p>
            <div className="grid md:grid-cols-2 gap-2">
              {service.features.map((feat, i) => (
                <div
                  key={i}
                  onMouseEnter={() => setActiveFeature(i)}
                  onMouseLeave={() => setActiveFeature(null)}
                  className={`flex items-start gap-3 px-4 py-3 rounded-xl border transition-all duration-200 cursor-default ${
                    activeFeature === i
                      ? "border-[#e8341c] bg-red-50"
                      : "border-gray-100 bg-gray-50 hover:border-gray-200"
                  }`}
                >
                  <span
                    className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 transition-colors duration-200 ${
                      activeFeature === i ? "bg-[#e8341c]" : "bg-gray-400"
                    }`}
                  />
                  <span className="text-sm text-gray-700 font-medium leading-snug">
                    {feat}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="flex gap-3 flex-wrap justify-center items-center pt-2">
            <button
              onClick={() => {
                const el = document.getElementById("contact");
                if (el) el.scrollIntoView({ behavior: "smooth" });
                else navigate("/contact");
              }}
              className="bg-[#e8341c] hover:bg-[#c42b14] text-white font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-red-200 active:scale-95"
            >
              Get a Quote
            </button>
            <button
              onClick={() => {
                navigate("/contact");
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }}
              className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-lg transition-all duration-200 active:scale-95"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* ══ SPECIALIZATIONS ══ */}
      <section
        className="w-full py-10 sm:py-16 px-4 sm:px-8 lg:px-16"
        style={{ backgroundColor: "#f5f0e8" }}
      >
        <div className="max-w-7xl mx-auto ">
          <div className="mb-10 flex flex-col items-start">
            <p className="text-xs font-bold font-krona tracking-[0.22em] uppercase text-gray-700 border-b-2 border-[#e8341c] inline-block pb-0.5 mb-3">
              Our Specializations
            </p>
            <h2 className="text-2xl md:text-4xl lg:text-[45px] font-krona mt-2 font-bold text-gray-900 leading-relaxed">
              Areas of Deep Expertise
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {service.specializations.map((spec, i) => (
              <div
                key={i}
                className="group relative bg-white rounded-2xl p-7 shadow-sm hover:shadow-xl transition-all duration-300 border border-transparent hover:border-[#e8341c]/20 overflow-hidden"
              >
                {/* Number watermark */}
                <span
                  className="absolute top-4 right-6 text-[80px] font-black text-gray-100 leading-none select-none group-hover:text-red-50 transition-colors duration-300"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>

                {/* Red top bar */}
                <div className="w-10 h-1 bg-[#e8341c] rounded mb-4 group-hover:w-16 transition-all duration-300" />

                <h3
                  className="text-xl font-black text-gray-900 mb-3 relative z-10"
                  style={{
                    fontFamily: "'Barlow Condensed', 'Arial Black', sans-serif",
                  }}
                >
                  {spec.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed relative z-10">
                  {spec.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PROCESS STRIP ══ */}
      <section className="w-full bg-white py-10 sm:py-16 px-4 sm:px-6 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-krona mt-2 font-bold text-gray-900 mb-10">
            Project Execution Methodology
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
            {steps.map((step, index) => {
              const Icon = step.icon;

              return (
                <div key={index} className="text-left">
                  {/* Icon */}
                  <div className={`text-4xl mb-3 ${step.color}`}>
                    <Icon />
                  </div>

                  {/* Dotted Line */}
                  <div
                    className={`w-16 border-t-2 border-dotted mb-4 ${step.border}`}
                  ></div>

                  {/* Title */}
                  <h3 className={`font-semibold mb-2 ${step.color}`}>
                    {step.title}
                  </h3>

                  {/* Points */}
                  <ul className="text-sm text-gray-600 space-y-1">
                    {step.points.map((point, i) => (
                      <li key={i}>• {point}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ OTHER SERVICES ══ */}
      <section className=" px-4 sm:px-6 lg:px-16 py-10 sm:py-16">
        <div className="flex items-end justify-between mb-10 max-w-7xl mx-auto">
          <div>
            <p className="text-xs font-bold tracking-[0.22em] uppercase text-gray-400 border-b-2 border-[#e8341c] inline-block pb-0.5 mb-3">
              Explore More
            </p>
            <h2
              className="text-4xl font-black text-gray-900"
              style={{
                fontFamily: "'Barlow Condensed', 'Arial Black', sans-serif",
                letterSpacing: "-0.02em",
              }}
            >
              Other Services
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto">
          {others.map((svc) => (
            <button
              key={svc.slug}
              onClick={() => navigate(`/services/${svc.slug}`)}
              className="group relative overflow-hidden rounded-2xl text-left bg-gray-50 border border-gray-100 hover:border-[#e8341c]/30 transition-all duration-300 hover:shadow-xl"
            >
              {/* Image */}
              <div className="relative overflow-hidden aspect-video">
                <img
                  src={svc.images[0]}
                  alt={svc.lable}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src =
                      "https://images.unsplash.com/photo-1578662996442-48f60103fc86?w=600&q=70";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <span className="absolute top-3 left-3 text-xl">
                  {svc.icon}
                </span>
              </div>

              {/* Text */}
              <div className="p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#e8341c] mb-1">
                  {svc.category}
                </p>
                <h3
                  className="text-base font-black text-gray-900 group-hover:text-[#e8341c] transition-colors duration-200 leading-tight"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {svc.lable}
                </h3>
                <p className="text-xs text-gray-400 mt-1 line-clamp-2 leading-relaxed">
                  {svc.shortDescription}
                </p>
              </div>

              {/* Arrow */}
              <div className="absolute bottom-4 right-4 w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center group-hover:bg-[#e8341c] group-hover:border-[#e8341c] transition-all duration-200">
                <svg
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                  className="text-gray-500 group-hover:text-white transition-colors duration-200"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ── Global keyframes ── */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes imgFadeIn {
          from { opacity: 0; transform: scale(1.03); }
          to   { opacity: 1; transform: scale(1); }
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
